import FormLoading from '@ui-framework/components/AdvancedForms/FormLoading';
import { FormattedMessage } from 'react-intl';
import { useDataProvider } from '@ui-framework/index';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useServiceProvider } from './ServiceProviderProvider';

const ServiceProviderLocationNameContext = createContext(null);

function ServiceProviderLocationProvider({ children }) {
  const [spLocation, setSPLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const { serviceProviderId, locationId } = useServiceProvider();

  const fetchServiceProviderLocation = async () => {
    const url = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers/${serviceProviderId}/locations/${locationId}`;
    const response = await dataProvider.get(url);

    if (response?.ok && response?.data?.locationName != null) {
      setSPLocation(response.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (serviceProviderId && locationId) {
      fetchServiceProviderLocation();
    } else {
      setLoading(false);
    }
  }, [serviceProviderId, locationId, dataProvider]);

  const locationNameContextValue = useMemo(
    () => ({
      spLocation,
      setSPLocation,
    }),
    [spLocation, setSPLocation],
  );

  if (loading) {
    return (
      <FormLoading>
        <FormattedMessage id="LOADING" defaultMessage="Loading" />
        ...
      </FormLoading>
    );
  }

  return (
    <ServiceProviderLocationNameContext.Provider
      value={locationNameContextValue}>
      {children}
    </ServiceProviderLocationNameContext.Provider>
  );
}

export const useServiceProviderLocation = () => {
  const context = useContext(ServiceProviderLocationNameContext);
  return context;
};

export function withServiceProviderLocation(WrappedComponent) {
  function withServiceProviderLocationProvider(props) {
    return (
      <ServiceProviderLocationProvider>
        <WrappedComponent {...props} />
      </ServiceProviderLocationProvider>
    );
  }
  withServiceProviderLocationProvider.displayName = `withServiceProviderLocation(${
    WrappedComponent?.displayName ?? WrappedComponent?.name ?? 'Component'
  })`;
  return withServiceProviderLocationProvider;
}

export default ServiceProviderLocationProvider;
